import { useCallback, useEffect, useMemo, useState } from 'react';
import { PasswordInput } from './PasswordInput';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { passwordStrength, type DiversityType } from '@framework/passwordStrength';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

const MinimumPasswordLength = 8;

const diversityKeyMap: Record<DiversityType, ParseKeys> = {
  lowercase: 'auth.editPasswordForm.diversity.lowercase',
  uppercase: 'auth.editPasswordForm.diversity.uppercase',
  number: 'auth.editPasswordForm.diversity.number',
  symbol: 'auth.editPasswordForm.diversity.symbol',
};

const diversities = Object.keys(diversityKeyMap) as DiversityType[];

type Props = {
  autoFocus?: boolean;
  disabled?: boolean;
  onChange(password: string): void;
};

export const EditPasswordForm: React.FC<Props> = ({ autoFocus, disabled, onChange }: Props) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // パスワードの強度チェック
  const strength = useMemo(() => passwordStrength(password), [password]);
  const nextRequiredDiversity = useMemo<DiversityType | null>(() => {
    if (password.length === 0) return null;
    return diversities.find((diversity) => !strength.contains.includes(diversity)) || null;
  }, [password, strength]);

  const handleChangePassword = useCallback((password: string) => {
    setPassword(password);
  }, []);
  const handleChangePasswordConfirm = useCallback((password: string) => {
    setPasswordConfirm(password);
  }, []);

  useEffect(() => {
    if (password.length > 0 && password.length < MinimumPasswordLength) {
      setError(t('auth.editPasswordForm.validation.minLength'));
      return;
    }

    if (nextRequiredDiversity) {
      setError(
        t('auth.editPasswordForm.validation.diversityRequired', {
          nextRequiredDiversity: t(diversityKeyMap[nextRequiredDiversity]),
        })
      );
      return;
    }

    setError(null);
  }, [password, nextRequiredDiversity, t]);

  const validateOnBlur = useCallback(() => {
    if (password.length >= MinimumPasswordLength && passwordConfirm && password !== passwordConfirm) {
      setConfirmError(t('auth.editPasswordForm.validation.confirmMismatch'));
    } else {
      setConfirmError(null);
    }
  }, [password, passwordConfirm, t]);

  useEffect(() => {
    // 入力エラーがなく、パスワードと確認用パスワードが一致している場合には、 password を上位コンポーネントに伝える。
    if (error === null && confirmError === null && password === passwordConfirm) {
      onChange(password);
    } else {
      onChange('');
    }
  }, [confirmError, error, onChange, password, passwordConfirm]);

  return (
    <>
      <div className="pb-2 pt-8 font-bold">{t('auth.editPasswordForm.form.newPasswordLabel')}</div>
      <PasswordInput
        value={password}
        onChange={handleChangePassword}
        error={error}
        onBlur={validateOnBlur}
        autoFocus={autoFocus}
        disabled={disabled}
      />
      <PasswordStrengthIndicator password={password} />

      <div className="pb-2 pt-8 font-bold">{t('auth.editPasswordForm.form.confirmLabel')}</div>
      <PasswordInput
        value={passwordConfirm}
        onChange={handleChangePasswordConfirm}
        error={confirmError}
        onBlur={validateOnBlur}
        disabled={disabled}
      />
    </>
  );
};
